// src/components/Sidebar/Sidebar.jsx
import React from 'react';
import { ChevronLeft } from 'lucide-react';
import PermitAreaSearch from './PermitAreaSearch';
import ZoneCreatorPanel from './ZoneCreatorPanel.jsx';
import OpenStreetsPanel from './OpenStreetsPanel.jsx';
import { useZoneCreatorContext } from '../../contexts/ZoneCreatorContext.jsx';
import LayersPanel from './LayersPanel';
import BasemapToggle from './BasemapToggle';
import { useGeoclientSearch } from '../../hooks/useGeoclientSearch';
import { searchGeoclient } from '../../services/geoclientService';
import { useGeoclientAuth } from '../../contexts/GeoclientAuthContext.jsx';

const Sidebar = ({ 
  layers,
  focusedArea,
  onClearFocus,
  onToggleLayer,
  permitAreas,
  infrastructure,
  map,
  onStyleChange,
  isSitePlanMode = false,
  geographyType,
  onCollapse = () => {},
  drawTools
}) => {
  const { isActive, setIsActive, previewActive } = useZoneCreatorContext();
  
  // In intersections (Open Streets) mode, hide infrastructure layers until zone is created
  const isZoneCreatorMode = geographyType === 'intersections';
  const showInfrastructureLayers = !isZoneCreatorMode || previewActive || isSitePlanMode;
  const { key: geoclientKey } = useGeoclientAuth();
  const geoSearch = useGeoclientSearch(permitAreas.searchQuery, {
    debounceMs: 300,
    limit: 8,
    options: {
      key: geoclientKey,
      // Improve partial-name tolerance and suggestions
      similarNamesDistance: 12, // default is 8
      exactMatchMaxLevel: 6,    // explore more sub-search levels
      returnPossiblesWithExact: true,
      exactMatchForSingleSuccess: false
    }
  });
  return (
    <div className={`${isSitePlanMode ? 'w-80' : 'w-96'} bg-white/90 dark:bg-gray-900/90 backdrop-blur-md dark:text-gray-100 shadow-lg z-10 flex flex-col transition-all duration-300 h-full relative border-r border-gray-200/50 dark:border-gray-700/50`}>
      <div className="relative">
        <BasemapToggle 
          map={map}
          onStyleChange={onStyleChange}
        />
        {/* Collapse control placed between Basemap and Search panels */}
        <button
          type="button"
          onClick={onCollapse}
          aria-label="Collapse sidebar"
          className="absolute -right-3 -bottom-4 z-20 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/60 rounded-r px-1 py-2 shadow-sm hover:bg-gray-100/90 dark:hover:bg-gray-700/90"
          title="Hide sidebar"
        >
          <ChevronLeft className="w-4 h-4 text-gray-700" />
        </button>
      </div>

      {/* Search panel, customized per geography; in intersections mode also show Zone Creator */}
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
        isSitePlanMode 
          ? 'max-h-0 opacity-0 transform -translate-y-2' 
          : 'max-h-96 opacity-100 transform translate-y-0'
      }`}>
          <PermitAreaSearch
          searchQuery={permitAreas.searchQuery}
          onSearchChange={permitAreas.setSearchQuery}
          searchResults={permitAreas.searchResults}
          isSearching={permitAreas.isSearching}
          onSelectArea={geographyType === 'intersections'
            ? ((feature) => {
                try {
                  const geom = feature?.geometry;
                  if (geom && geom.type === 'Point' && map && map.easeTo) {
                    map.easeTo({ center: geom.coordinates, duration: 600, essential: true });
                  }
                } catch (_) {}
              })
            : permitAreas.focusOnPermitArea}
          focusedArea={focusedArea}
          title={geographyType === 'intersections' ? 'Search Intersections' : 'Search Zones'}
          placeholder={geographyType === 'intersections' ? 'Search intersections...' : 'Search zones...'}
          onChangeMode={() => {
            // Show the geography selector modal via custom event (handled in SpaceStager)
            // Simpler approach: reset focus and let the top-level selector open on next render
            try { permitAreas.clearFocus(); } catch (_) {}
            try {
              const evt = new CustomEvent('ui:show-geography-selector');
              window.dispatchEvent(evt);
            } catch (_) {}
          }}
          permitAreasLayer={layers?.permitAreas}
          onToggleLayer={onToggleLayer}
          geographyType={geographyType}
            geoclientResults={geoSearch.results}
            geoclientLoading={geoSearch.isLoading}
            geoclientStatus={geoSearch.status}
            geoclientError={geoSearch.error}
            geoclientCooldownMs={geoSearch.cooldownMs}
            onSelectGeoclientResult={async (item) => {
              try {
                if (item && Array.isArray(item.coords) && item.coords.length >= 2 && map && map.easeTo) {
                  map.easeTo({ center: item.coords, zoom: 18, duration: 600, essential: true });
                  return;
                }
                // Fallback: resolve suggestion to a geocoded result and pan
                const label = item && item.label ? String(item.label) : '';
                if (!label) return;
                const out = await searchGeoclient({ input: label, limit: 1 });
                const first = Array.isArray(out?.results) ? out.results.find(r => Array.isArray(r.coords)) : null;
                if (first && map && map.easeTo) {
                  map.easeTo({ center: first.coords, zoom: 18, duration: 600, essential: true });
                }
              } catch (_) {}
            }}
        />
      </div>

      {/* Open Streets Events Panel - Only in SAPO modes, hidden in design mode */}
      {['openStreets', 'plazas', 'intersections'].includes(geographyType) && !focusedArea && (
        <div className="px-3 py-2">
          <OpenStreetsPanel focusedArea={focusedArea} />
        </div>
      )}

      {geographyType === 'intersections' && (
        <div className={`transition-all duration-300 ease-in-out overflow-hidden opacity-100 transform translate-y-0 ${
          showInfrastructureLayers ? 'max-h-96' : 'flex-1 min-h-0'
        }`}>
          <ZoneCreatorPanel geographyType={geographyType} showExpanded={!showInfrastructureLayers} />
        </div>
      )}

      {/* Infrastructure Layers - Hidden in Zone Creator mode until design mode */}
      {showInfrastructureLayers && (
        <div className="flex-1 min-h-0">
          <LayersPanel
            layers={layers}
            focusedArea={focusedArea}
            onToggleLayer={onToggleLayer}
            onToggleSubwayLines={onToggleLayer}
            onClearFocus={onClearFocus}
            isSitePlanMode={isSitePlanMode}
            geographyType={geographyType}
            map={map}
            infrastructure={infrastructure}
            permitAreas={permitAreas}
            hasSubFocus={permitAreas?.hasSubFocus}
            onBeginSubFocus={() => {
              try { drawTools?.activateDrawingTool?.('subfocus'); } catch (_) {}
            }}
            onClearSubFocus={() => {
              try { permitAreas?.clearSubFocusPolygon?.(); } catch (_) {}
            }}
          />
        </div>
      )}

      {/* Progress modal removed (replaced by inline panel indicator) */}
    </div>
  );
};

export default Sidebar;